import React from 'react';
import {Button, Icon, Tag} from '@mc/ui-v4';
import {mcFilterTable} from '@mc/react-ui-icons';
import {CheckState, CiselnikFilterNode} from './filterTreeTypes';
import {UseFilterTreeResult} from './useFilterTree';
import './FilterTagsBar.scss';

type Props = {
    /** Kořeny stromu filtru. */
    tree: CiselnikFilterNode[];
    /** Stav stromu (zaškrtnutí). */
    filterTree: UseFilterTreeResult;
    /** Otevře režim „Nastavení filtru". */
    onOpenSettings: () => void;
};

/**
 * Vybere uzly, které se mají vypsat jako tagy zafiltrovaných hodnot: nejnižší úroveň, která je vybraná CELÁ.
 *
 * Uživatel typicky zaškrtává konkrétní podkategorie (2. úroveň), a tag pak musí nést jejich název – ne název
 * naražené nadřazené kategorie. Nadřazená kategorie se použije jen tehdy, když jsou vybrané všechny její
 * podřízené hodnoty (pak je jeden tag srozumitelnější než výčet všech potomků).
 *
 * Prakticky: uzel se stavem „checked" dá jeden tag; „indeterminate" se rozbalí na potomky (rekurzivně, takže
 * pravidlo platí i pro hlubší stromy); „unchecked" se přeskočí.
 */
const collectAttributeTags = (nodes: CiselnikFilterNode[], getState: (node: CiselnikFilterNode) => CheckState): CiselnikFilterNode[] =>
    nodes.flatMap(node => {
        const state = getState(node);
        if (state === 'unchecked') {
            return [];
        }
        if (state === 'checked' || !node.children || node.children.length === 0) {
            return [node];
        }
        return collectAttributeTags(node.children, getState);
    });

/**
 * Lišta nad seznamem objektů se zafiltrovanými hodnotami jako tagy + trychtýř do „Nastavení filtru".
 *
 * Strom má dvě dimenze rozlišené znaménkem id (viz {@code buildCatalogFilterNodes}):
 *  - VRSTVY z katalogu (záporná id) – zploštělé koncové vrstvy. Když jsou zaškrtnuté VŠECHNY, je to
 *    parita s katalogem (žádné omezení) → nezobrazí se žádný vrstvový tag. Jakmile nějakou vrstvu
 *    odškrtnu, vypíšou se jako tagy ty vrstvy, podle kterých se filtruje (tj. zbylé zaškrtnuté).
 *  - ATRIBUTOVÉ hodnoty z {@code getSearchFilterTree} (kladná id) – přidávají se jako tagy vždy (i když jsou
 *    všechny vrstvy zaškrtnuté), a to v té nejnižší úrovni, která je vybraná celá (viz {@link collectAttributeTags}).
 *
 * Křížek na tagu odškrtne celý jeho podstrom ({@link UseFilterTreeResult.clearNode}). Když není zaškrtnuté
 * nic (výchozí stav i po „Vynulovat filtr"), nefiltruje se a lišta je prázdná – jen tlačítko „Filtrovat".
 */
export const FilterTagsBar: React.FC<Props> = ({tree, filterTree, onOpenSettings}) => {
    const {getState, clearNode} = filterTree;
    // Katalogové (vrstvové) uzly mají záporná id, atributové (ze serveru) kladná.
    const layerTags = tree.filter(node => node.id < 0 && getState(node) === 'checked');
    const attributeTags = collectAttributeTags(
        tree.filter(node => node.id > 0),
        getState,
    );
    const tags = [...layerTags, ...attributeTags];

    return (
        <div className={'filter-tags-bar'}>
            {/* Tlačítko filtru je primary a fixně vlevo; zafiltrované hodnoty (tagy) se rolují horizontálně vpravo. */}
            <Button
                className={'filter-tags-btn'}
                style={`${tags.length === 0 ? 'default' : 'primary'}`}
                title={'Nastavení filtru'}
                icon={<Icon icon={mcFilterTable} />}
                onClick={onOpenSettings}
            >
                {tags.length === 0 ? 'Filtrovat' : ''}
            </Button>
            <div className={'filter-tags'}>
                {tags.map(node => (
                    <Tag key={node.id} size={'lg'} intent={'default'} inverse closable onClose={() => clearNode(node)}>
                        {node.label}
                    </Tag>
                ))}
            </div>
        </div>
    );
};
